import { Injectable } from '@angular/core';
import { SupplierConfig } from '../models/supplier-config';

/**
 * Supplier Registry Service
 * Manages supplier configurations and provides access to supplier settings
 * Supports dynamic registration and retrieval of supplier integrations
 */
@Injectable({
  providedIn: 'root'
})
export class SupplierRegistryService {
  private suppliers: Map<string, SupplierConfig> = new Map();

  constructor() {
    this.initializeDefaultSuppliers();
  }

  /**
   * Initialize default supplier configurations
   * These will be updated with actual enabled status from API
   */
  private initializeDefaultSuppliers(): void {
    // Hyperpure configuration
    this.register({
      id: 'hyperpure',
      name: 'Hyperpure',
      color: '#FFF5F5',
      logoUrl: 'assets/images/hyperpure-logo.png',
      enabled: false,
      isOrderingEnabled: true, // Ordering is enabled for Hyperpure
      apiEndpoints: {
        checkEnabled: 'check-hyperpure-enabled',
        search: 'search',
        compare: 'purchase-manager',
        addToCart: 'cart',
        getCart: 'cart',
        proceedToBuy: 'proceed-to-buy'
      },
      cartKey: 'hyperpureCart',
      iframeUrl: 'https://www.hyperpure.com/in/Init',
      deliveryMode: 'DELIVERY'
    });

    // Dmart configuration
    this.register({
      id: 'dmart',
      name: 'Dmart',
      color: '#e7fde3ff',
      logoUrl: 'assets/images/d-mart-logo.png',
      enabled: false,
      isOrderingEnabled: true, // Ordering is enabled for DMart
      apiEndpoints: {
        checkEnabled: 'check-dmart-enabled',
        compare: 'purchase-manager', // Same endpoint, handles multiple suppliers
        addToCart: 'dmart/cart',
        getCart: 'dmart/cart'
      },
      cartKey: 'dmartCart',
      deliveryMode: 'DELIVERY'
    });

    // Udaan configuration
    this.register({
      id: 'udaan',
      name: 'Udaan',
      color: '#FEF3E2',
      logoUrl: 'assets/images/udaan-log.png',
      enabled: false,
      isOrderingEnabled: true, // Ordering is enabled for Udaan
      isLocalSupplier: false,  // ✅ Udaan is NOT a local supplier - it's an integrated supplier
      apiEndpoints: {
        checkEnabled: 'check-udaan-enabled',
        compare: 'purchase-manager', // Same endpoint, handles multiple suppliers
        addToCart: 'udaan/cart/update',  // POST to update cart
        getCart: 'udaan/cart'           // GET to retrieve cart
      },
      cartKey: 'udaanCart',
      deliveryMode: 'DELIVERY'
    });
  }

  /**
   * Register a new supplier configuration
   * @param config Supplier configuration object
   */
  register(config: SupplierConfig): void {
    this.suppliers.set(config.id, config);
  }

  /**
   * Get a specific supplier configuration by ID
   * @param id Supplier ID (e.g., 'hyperpure', 'dmart')
   * @returns Supplier configuration or undefined
   */
  get(id: string): SupplierConfig | undefined {
    return this.suppliers.get(id);
  }

  /**
   * Get all registered suppliers
   * @returns Array of all supplier configurations
   */
  getAll(): SupplierConfig[] {
    return Array.from(this.suppliers.values());
  }

  /**
   * Get only enabled suppliers
   * @returns Array of enabled supplier configurations
   */
  getEnabled(): SupplierConfig[] {
    return Array.from(this.suppliers.values()).filter(s => s.enabled);
  }

  /**
   * Check if a supplier is enabled
   * @param id Supplier ID
   * @returns True if supplier is enabled, false otherwise
   */
  isEnabled(id: string): boolean {
    const supplier = this.suppliers.get(id);
    return supplier ? supplier.enabled : false;
  }

  /**
   * Update supplier enabled status
   * @param id Supplier ID
   * @param enabled New enabled status
   */
  setEnabled(id: string, enabled: boolean): void {
    const supplier = this.suppliers.get(id);
    if (supplier) {
      supplier.enabled = enabled;
    }
  }

  /**
   * Update supplier store configuration
   * @param id Supplier ID
   * @param storeConfig Store configuration object
   */
  updateStoreConfig(id: string, storeConfig: any): void {
    const supplier = this.suppliers.get(id);
    if (supplier) {
      supplier.storeConfig = {
        storeUuid: storeConfig.storeUuid || storeConfig.store_uuid || '',
        stores: storeConfig.stores || [],
        selectedStoreId: storeConfig.selectedStoreId || storeConfig.stores?.[0]?.store_id
      };
    }
  }

  /**
   * Update supplier login data
   * @param id Supplier ID
   * @param loginData Login/authentication data
   */
  updateLoginData(id: string, loginData: any): void {
    const supplier = this.suppliers.get(id);
    if (supplier) {
      supplier.loginData = loginData;
    }
  }

  /**
   * Update supplier fetching state
   * @param id Supplier ID
   * @param isFetching Loading state
   */
  setFetching(id: string, isFetching: boolean): void {
    const supplier = this.suppliers.get(id);
    if (supplier) {
      supplier.isFetching = isFetching;
    }
  }

  /**
   * Clear all supplier data (useful for logout/reset)
   */
  clearAll(): void {
    this.suppliers.forEach(supplier => {
      supplier.enabled = false;
      supplier.storeConfig = undefined;
      supplier.loginData = undefined;
      supplier.isFetching = false;
    });
  }

  /**
   * Get supplier by column ID (for backwards compatibility)
   * @param columnId Column ID from supplier columns
   * @returns Supplier configuration or undefined
   */
  getByColumnId(columnId: string): SupplierConfig | undefined {
    return this.suppliers.get(columnId);
  }

  /**
   * Get supplier color by ID
   * @param id Supplier ID
   * @returns Color hex code or default color
   */
  getColor(id: string): string {
    const supplier = this.suppliers.get(id);
    return supplier ? supplier.color : '#F5F5F5';
  }

  /**
   * Get supplier name by ID
   * @param id Supplier ID
   * @returns Supplier display name or ID
   */
  getName(id: string): string {
    const supplier = this.suppliers.get(id);
    return supplier ? supplier.name : id;
  }

  /**
   * Get cart storage key for supplier
   * @param id Supplier ID
   * @returns LocalStorage key for cart
   */
  getCartKey(id: string): string {
    const supplier = this.suppliers.get(id);
    return supplier ? supplier.cartKey : `${id}Cart`;
  }

  /**
   * Register a local supplier from Add Supplier form
   * Local suppliers use Create PO flow instead of cart
   * @param supplierData Supplier data from API response
   * @returns SupplierConfig The registered supplier configuration
   */
  registerLocalSupplier(supplierData: any): SupplierConfig {
    // Generate supplier ID from name (lowercase, replace spaces with hyphens)
    const supplierId = supplierData.id ||
      (supplierData.name || supplierData.supplierName || 'supplier')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');

    const config: SupplierConfig = {
      id: supplierId,
      name: supplierData.name || supplierData.supplierName || 'Local Supplier',
      color: '#F5F5F5', // Default gray color
      logoUrl: 'assets/images/local-suppliers-logo.png', // Default supplier logo
      enabled: true,
      isOrderingEnabled: true, // Enable ordering via Create PO
      isLocalSupplier: true,   // Mark as local supplier
      apiEndpoints: {
        checkEnabled: '',
        compare: '',
        addToCart: '',
        getCart: ''
      },
      cartKey: `${supplierId}Cart`,
      supplierDetails: {
        gstNo: supplierData.gstNo || supplierData.gst_no,
        alias: supplierData.alias || supplierData.supplier_alias,
        mobile: supplierData.mobile || supplierData.mobile_number,
        email: supplierData.email,
        ...supplierData
      }
    };

    // Register the supplier
    this.register(config);
    console.log(`✅ Registered local supplier: ${config.name} (${config.id})`);

    return config;
  }

  /**
   * Check if a supplier is a local supplier
   * @param id Supplier ID
   * @returns True if supplier is local, false otherwise
   */
  isLocalSupplier(id: string): boolean {
    const supplier = this.suppliers.get(id);
    return supplier ? supplier.isLocalSupplier === true : false;
  }
}
